import classNames from "classnames";
import React, { memo, useMemo, type HTMLAttributes, type ReactNode } from "react";
import { useTranslationContext } from "../../context";
import ValidationMessage, { ValidationMessageType } from "./validationMessage";

/**
 * Props for InputWrapperWithHeading component.
 * @property id - The id for the input element
 * @property label - The label text for the input
 * @property errorMessage - Error message to display
 * @property warningMessage - Warning message to display
 * @property description - Description/help text for the input
 * @property additionalClass - Additional CSS classes for the wrapper
 * @property additionalLabelClass - Additional CSS classes for the label
 * @property additionalDescClass - Additional CSS classes for the description
 * @property required - Whether the field is required
 * @property invalid - Whether the field is invalid
 * @property warning - Whether the field has a warning
 * @property children - Child input elements
 * @property additionalAttributes - Additional HTML attributes for the wrapper
 */
interface InputWrapperWithHeadingProps {
  id?: string;
  label?: string;
  errorMessage?: string;
  warningMessage?: string;
  description?: string;
  additionalClass?: string;
  additionalLabelClass?: string;
  additionalDescClass?: string;
  required?: boolean;
  invalid?: boolean;
  warning?: boolean;
  children?: ReactNode;
  additionalAttributes?: HTMLAttributes<HTMLDivElement>;
}

/**
 * InputWrapperWithHeading wraps an input field with label, description, and error/warning display.
 * Applies consistent styling and accessibility features for form fields.
 *
 * **Accessibility Features:**
 * - Links error/warning messages to inputs via aria-describedby
 * - Provides unique IDs for error and description elements
 * - Marks required fields appropriately
 * - Uses semantic HTML (label, span with role="alert")
 */
const InputWrapperWithHeading = memo(function InputWrapperWithHeading({
  id,
  label,
  errorMessage,
  warningMessage,
  description,
  additionalClass = "",
  additionalLabelClass = "",
  additionalDescClass = "",
  required = false,
  invalid = false,
  warning = false,
  children,
  additionalAttributes
}: InputWrapperWithHeadingProps) {
  const translationContext = useTranslationContext();

  // Generate IDs for aria-describedby linking
  const errorId = useMemo(() => (id ? `${id}-error` : undefined), [id]);
  const warningId = useMemo(() => (id ? `${id}-warning` : undefined), [id]);
  const descriptionId = useMemo(() => (id ? `${id}-description` : undefined), [id]);

  // Build aria-describedby value based on what's visible
  const ariaDescribedBy = useMemo(() => {
    const parts: string[] = [];

    if (description && descriptionId) {
      parts.push(descriptionId);
    }

    if (invalid && errorId) {
      parts.push(errorId);
    } else if (warning && warningId) {
      parts.push(warningId);
    }

    return parts.length > 0 ? parts.join(" ") : undefined;
  }, [description, descriptionId, invalid, errorId, warning, warningId]);

  const controlClasses = classNames(
    "relative group/input-wrapper",
    {
      "validation-error": invalid,
      "validation-warning": warning
    },
    additionalClass
  );

  const controlAttributes = {
    "data-validation-error": invalid ? "true" : undefined,
    "data-validation-warning": warning ? "true" : undefined
  };

  const labelClasses = classNames(
    "font-guardian text-t-sm font-t-semi-bold text-t-neutral-800",
    additionalLabelClass
  );

  const descriptionClasses = classNames(
    "font-guardian text-t-xs font-normal text-t-neutral-700",
    additionalDescClass
  );

  const optionalBadgeClasses = classNames(
    "flex flex-row items-center relative text-t-xxs font-t-semi-bold",
    "uppercase tracking-[1px] text-t-neutral-600 before:content-['']",
    "before:w-xxs before:h-xxs before:mr-xxs before:rounded-full before:bg-turquoise-800"
  );

  // Clone children and pass aria-describedby if available
  const enhancedChildren = useMemo(() => {
    // Always go through React.Children.map, even when there is nothing to add. It assigns
    // implicit keys to the children, so returning the raw `children` in one case would
    // change the child's key when aria-describedby appears (e.g. on first validation
    // error), and remount it. That resets stateful children, such as closing an open
    // SelectInput dropdown.
    if (ariaDescribedBy) {
      return React.Children.map(children, child =>
        React.isValidElement<React.AriaAttributes>(child)
          ? React.cloneElement(child, {
              "aria-describedby": ariaDescribedBy,
              "aria-invalid": invalid ? "true" : undefined,
              "aria-required": required ? "true" : undefined
            })
          : child
      );
    } else {
      return React.Children.map(children, child => child);
    }
  }, [children, ariaDescribedBy, invalid, required]);

  return (
    <div className="flex flex-col flex-1 basis-full">
      <div className="flex flex-col">
        <div className="flex flex-row justify-between mb-xs">
          <div className="flex flex-col">
            <label htmlFor={id} className={labelClasses}>
              {label}
            </label>

            {description && (
              <div id={descriptionId} className={descriptionClasses}>
                {description}
              </div>
            )}
          </div>

          {!required && label && (
            <span className="flex place-items-center mt-[calc(1ex-1cap)]">
              <span aria-hidden="true" className={optionalBadgeClasses}>
                {translationContext?.translations?.optional}
              </span>
            </span>
          )}
        </div>

        <div className={controlClasses} {...controlAttributes} {...additionalAttributes}>
          {enhancedChildren}

          {invalid && <ValidationMessage id={errorId} message={errorMessage} />}
          {warning && !invalid && (
            <ValidationMessage
              id={warningId}
              type={ValidationMessageType.WARNING}
              message={warningMessage}
            />
          )}
        </div>
      </div>
    </div>
  );
});

export default InputWrapperWithHeading;
